import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useForm as useHookForm } from 'react-hook-form'
// import { DevTool } from '@hookform/devtools'
import PropTypes from 'prop-types'
import {
  updateFormSuccess,
  resetFormSuccess,
  setFieldValueSuccess,
  getFormValuesSuccess,
  filterEmpty,
  intersection
} from '../'

/**
 *
 * @param {Object} options.defaultValues Default form values.
 *  Should contain a default value for every field in the form.
 * @param {function} options.validator Validator function
 * @param {string} options.mode Mode for triggering validation.
 *  Defaults to 'onBlur'.
 * @returns
 */
export const useForm = (options) => {
  const { defaultValues, validator, mode = 'onBlur', ...rest } = options

  const form = useHookForm({
    mode: mode,
    defaultValues,
    resolver: validator,
    criteriaMode: 'all'
  })

  return { ...form, ...rest }
}

const Form = ({ style, ...props }) => {
  const {
    name,
    setValue,
    handleSubmit,
    reset,
    // control,
    formState: { isSubmitSuccessful },
    getValues,
    onSubmit,
    keepEmptyValuesOnSubmit,
    resetOnSubmit = true,
    children
  } = props

  const formData = useSelector((state) => state.form[name])
  const dispatch = useDispatch()

  const _onSubmit = (values) => {
    if (keepEmptyValuesOnSubmit) onSubmit(values)
    else onSubmit(filterEmpty(values))
  }

  React.useEffect(() => {
    if (isSubmitSuccessful && resetOnSubmit) {
      reset()
    }
  }, [isSubmitSuccessful, reset])

  React.useEffect(() => {
    if (formData?.shouldReset) {
      reset()
      dispatch(resetFormSuccess(name))
    }
  }, [formData?.shouldReset, name, reset])

  React.useEffect(() => {
    if (formData?.shouldGetValues) {
      dispatch(getFormValuesSuccess(name, getValues()))
    }
  }, [formData?.shouldGetValues, name, getValues])

  React.useEffect(() => {
    if (formData?.shouldLoad) {
      //should only reset with properties that are in defaultvalues
      const validFieldValues = intersection(formData.loadData, getValues())
      reset(validFieldValues, { keepDefaultValues: true })
      dispatch(updateFormSuccess(name))
    }
  }, [formData?.shouldLoad, formData?.loadData, name, reset])

  React.useEffect(() => {
    if (formData?.setValue) {
      if (typeof formData.setValue === 'object') {
        if (Array.isArray(formData.setValue)) {
          formData.setValue.forEach((el) => setValue(el.field, el.value))
        } else setValue(formData.setValue.field, formData.setValue.value)
      }
      dispatch(setFieldValueSuccess(name, formData.setValue.field))
    }
  }, [formData?.setValue, name, reset])

  return (
    <>
      <form onSubmit={handleSubmit(_onSubmit)} style={style}>
        {children}
      </form>
      {/* <DevTool control={control} /> */}
    </>
  )
}

Form.propTypes = {
  /** Name of the form */
  name: PropTypes.string.isRequired,
  /** Callback function for form submission */
  onSubmit: PropTypes.func.isRequired,
  // /**
  //  * Default form values. Should contain a default value
  //  * for every field in the form.
  //  */
  // defaultValues: PropTypes.object.isRequired,
  // /**
  //  *
  //  * @param values Form values
  //  * @returns An object of shape { values, errors}
  //  */
  // validator: PropTypes.func,
  // /**
  //  * Enables parent access to form methods and state (e.g. reset, formState.errors)
  //  * See {@link https://react-hook-form.com/api/useform/} for all available properties.
  //  */
  // setMethods: PropTypes.func,
  /**
   * Setting to retain empty values in the object
   * passed to the onSubmit function. False by default.
   */
  keepEmptyValues: PropTypes.bool,
  style: PropTypes.object 
}

export default Form
